import React, { useState, useEffect, useRef } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Row,
  Col,
  Input,
  Label,
  FormFeedback,
  Spinner,
  Button,
  Progress,
  Table,
} from "reactstrap";
import moment from "moment";
import { getToken, getUserRole } from "helpers/jwt-token-access/auth-token-header";
import {
  useGetChecklistMappingsByProductQuery,
  useUploadChecklistDocumentMutation,
} from "slices/InventoryManagement/inventoryManagementSlice"; // ← update path

interface Props {
  isOpen: boolean;
  toggle: () => void;
  purchaseRequestData: any;
  cartItems: any[];
  formErrors: Record<string, string>;
  isSubmitting: boolean;
  purchaseId?: string;
  onChange: (e: any) => void;
  onSubmit: () => void;
}

// ── Step Indicator ────────────────────────────────────────────────────────────
const StepIndicator: React.FC<{ currentStep: number; steps: string[] }> = ({
  currentStep,
  steps,
}) => (
  <div className="d-flex align-items-center mb-4">
    {steps.map((label, index) => {
      const stepNum = index + 1;
      const isCompleted = stepNum < currentStep;
      const isActive = stepNum === currentStep;

      return (
        <React.Fragment key={stepNum}>
          <div className="d-flex flex-column align-items-center">
            <div
              className="rounded-circle d-flex align-items-center justify-content-center fw-bold"
              style={{
                width: 36,
                height: 36,
                fontSize: 14,
                backgroundColor: isCompleted
                  ? "#0ab39c"
                  : isActive
                    ? "#405189"
                    : "#e9ebec",
                color: isCompleted || isActive ? "#fff" : "#878a99",
                border: isActive
                  ? "2px solid #405189"
                  : "2px solid transparent",
              }}
            >
              {isCompleted ? <i className="ri-check-line" /> : stepNum}
            </div>
            <small
              className="mt-1 text-center"
              style={{
                fontSize: 11,
                color: isActive
                  ? "#405189"
                  : isCompleted
                    ? "#0ab39c"
                    : "#878a99",
                fontWeight: isActive ? 600 : 400,
                maxWidth: 80,
              }}
            >
              {label}
            </small>
          </div>

          {index < steps.length - 1 && (
            <div
              className="flex-grow-1 mx-1"
              style={{
                height: 2,
                backgroundColor: isCompleted ? "#0ab39c" : "#e9ebec",
                marginBottom: 20,
              }}
            />
          )}
        </React.Fragment>
      );
    })}
  </div>
);

// ── Upload Cell per mapping ───────────────────────────────────────────────────
const UploadCell: React.FC<{
  mapping: any;
  purchaseRequestId?: number | string;
  onUploaded: (mappingId: number, done: boolean) => void;
}> = ({ mapping, purchaseRequestId, onUploaded }) => {
  const [uploadDocument] = useUploadChecklistDocumentMutation();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [existingDocuments, setExistingDocuments] = useState<any[]>([]);
  const [isLoadingDocuments] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [deletingDocId, setDeletingDocId] = useState<number | null>(null);
  const [error, setError] = useState("");
  const isUploaded = existingDocuments.length > 0;

  useEffect(() => {
    onUploaded(mapping.id, isUploaded);
  }, [mapping.id, isUploaded, onUploaded]);

  const handleFileChange = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    if (isUploaded) return;

    setIsUploading(true);
    setError("");
    const file = files[0];
    try {
      const formData = new FormData();
      formData.append("mapping", String(mapping.id));
      if (purchaseRequestId) {
        formData.append("purchase_request", String(purchaseRequestId));
      }
      formData.append("document_name", file.name);
      formData.append("attachment", file);
      const created = await uploadDocument(formData).unwrap();
      // For this PR flow, treat only documents uploaded in this session as fulfilling the requirement.
      setExistingDocuments([created]);
    } catch (err: any) {
      setError(err?.data?.error || `Failed to upload ${file.name}`);
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeleteDocument = async (documentId: number) => {
    setDeletingDocId(documentId);
    setError("");
    try {
      const token = getToken();
      const accessRole = getUserRole();
      const response = await fetch(
        `${window.__RUNTIME_CONFIG__.REACT_APP_API_URL}inventory/api/v1/checklist-documents/${documentId}/`,
        {
          method: "DELETE",
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...(accessRole ? { AccessRole: String(accessRole) } : {}),
          },
        },
      );
      if (!response.ok) {
        const result = await response.json();
        throw new Error(result?.error || "Failed to delete document.");
      }
      // Locally remove the document so the user can re-upload within this PR.
      setExistingDocuments((prev) => prev.filter((doc: any) => doc.id !== documentId));
    } catch (err: any) {
      setError(err?.message || "Failed to delete document.");
    } finally {
      setDeletingDocId(null);
    }
  };

  return (
    <div>
      {/* Hidden file input, controlled by upload button */}
      <Input
        innerRef={fileInputRef}
        type="file"
        className="d-none"
        bsSize="sm"
        multiple={false}
        onChange={(e) => handleFileChange(e.target.files)}
        disabled={isUploading || isUploaded || isLoadingDocuments}
      />
      <Button
        color={isUploaded ? "success" : "primary"}
        size="sm"
        outline={!isUploaded}
        onClick={() => fileInputRef.current?.click()}
        disabled={isUploading || isUploaded || isLoadingDocuments}
      >
        {isUploading ? (
          <>
            <Spinner size="sm" className="me-1" />
            Uploading...
          </>
        ) : isUploaded ? (
          <>
            <i className="ri-check-line me-1" />
            Uploaded
          </>
        ) : (
          <>
            <i className="ri-upload-2-line me-1" />
            Upload File
          </>
        )}
      </Button>

      {/* Existing uploaded file list */}
      {isLoadingDocuments && (
        <div className="text-muted mt-1" style={{ fontSize: 12 }}>
          <Spinner size="sm" className="me-1" />
          Loading uploaded file...
        </div>
      )}

      {existingDocuments.length > 0 && (
        <div className="mt-1">
          {existingDocuments.map((doc: any) => (
            <div
              key={doc.id}
              className="d-flex align-items-center justify-content-between gap-2"
              style={{ fontSize: 12 }}
            >
              <div className="text-success d-flex align-items-center gap-1">
                <i className="ri-check-line" />
                {doc.document_name || "Uploaded document"}
              </div>
              <button
                type="button"
                className="btn btn-sm btn-soft-danger py-0 px-1"
                onClick={() => handleDeleteDocument(doc.id)}
                disabled={deletingDocId === doc.id}
              >
                {deletingDocId === doc.id ? (
                  <Spinner size="sm" />
                ) : (
                  <i className="ri-delete-bin-line" />
                )}
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="text-danger mt-1" style={{ fontSize: 12 }}>
          {error}
        </div>
      )}
    </div>
  );
};

// ── Per-product rows in the table ─────────────────────────────────────────────
const ProductChecklistRows: React.FC<{
  item: any;
  purchaseRequestId?: number | string;
  onAllUploaded: (productId: number, done: boolean) => void;
}> = ({ item, purchaseRequestId, onAllUploaded }) => {
  const productId = item.product?.id ?? item.product;
  const productImage =
    item?.product?.thumbnail_url ||
    item?.product?.attachment_urls?.[0]?.file_url ||
    item?.product?.attachment_urls?.[0]?.file ||
    null;

  const { data: mappingsData, isLoading } =
    useGetChecklistMappingsByProductQuery(productId);
  const [uploadedMap, setUploadedMap] = useState<Record<number, boolean>>({});

  const mappings = mappingsData?.results ?? mappingsData ?? [];
  const requiredMappings = mappings.filter((m: any) => m.is_required);

  useEffect(() => {
    const next: Record<number, boolean> = {};
    requiredMappings.forEach((m: any) => {
      const uploadedCount = Number(
        m?.uploaded_files_count ?? m?.uploaded_documents?.length ?? 0,
      );
      next[m.id] = uploadedCount > 0;
    });
    setUploadedMap(next);
  }, [mappingsData]);

  const allDone =
    requiredMappings.length > 0 &&
    requiredMappings.every((m: any) => uploadedMap[m.id]);

  useEffect(() => {
    onAllUploaded(productId, requiredMappings.length === 0 ? true : allDone);
  }, [productId, allDone, requiredMappings.length, onAllUploaded]);

  const handleMappingUploaded = (mappingId: number, done: boolean) => {
    setUploadedMap((prev) => ({ ...prev, [mappingId]: done }));
  };

  if (isLoading) {
    return (
      <tr>
        <td colSpan={2} className="text-center py-3">
          <Spinner size="sm" className="me-2" />
          Loading documents for <strong>{item.product?.name}</strong>...
        </td>
      </tr>
    );
  }

  if (requiredMappings.length === 0) {
    return (
      <tr>
        <td className="align-middle fw-semibold ps-3">
          {item.product?.name}
          <br />
          <small className="text-muted fw-normal">Qty: {item.quantity}</small>
        </td>
        <td className="text-muted fst-italic align-middle">
          No required documents
        </td>
      </tr>
    );
  }

  return (
    <>
      {requiredMappings.map((mapping: any, index: number) => (
        <tr key={mapping.id}>
          {/* Product name cell — rowspan across all mapping rows */}
          {index === 0 && (
            <td
              rowSpan={requiredMappings.length}
              className="align-middle ps-3 border-end"
              style={{ verticalAlign: "middle", minWidth: 160 }}
            >
              <div className="d-flex align-items-start gap-2">
                <div
                  className="rounded border bg-light d-flex align-items-center justify-content-center"
                  style={{ width: 44, height: 44, overflow: "hidden", flexShrink: 0 }}
                >
                  {productImage ? (
                    <img
                      src={productImage}
                      alt={item.product?.name || "Product"}
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  ) : (
                    <i className="ri-image-line text-muted" />
                  )}
                </div>
                <div className="small">
                  <div className="fw-semibold">{item.product?.name}</div>
                  <div className="text-muted">Qty: {item.quantity}</div>
                  <div className="text-muted">
                    Category: {item.product?.category_name || "-"}
                  </div>
                  <div className="text-muted">
                    Unit: {item.product?.measurement_name || "-"}
                  </div>
                  <div className="text-muted">
                    {requiredMappings.filter((m: any) => uploadedMap[m.id]).length}/
                    {requiredMappings.length} uploaded
                  </div>
                </div>
              </div>
            </td>
          )}

          {/* Document upload cell */}
          <td className="py-2 px-3">
            <div className="d-flex align-items-start gap-3">
              <div style={{ minWidth: 130 }}>
                <div className="fw-semibold" style={{ fontSize: 13 }}>
                  {mapping.checklist_name ?? mapping.checklist?.document_name}
                  <span className="text-danger ms-1">*</span>
                </div>
                <div className="text-muted" style={{ fontSize: 11 }}>
                  Required document
                </div>
              </div>
              <div className="flex-grow-1">
                <UploadCell
                  mapping={mapping}
                  purchaseRequestId={purchaseRequestId}
                  onUploaded={handleMappingUploaded}
                />
              </div>
            </div>
          </td>
        </tr>
      ))}
    </>
  );
};

// ── Single upload per document type (for Update PR when purchaseId exists) ─────
const UploadByTypeCell: React.FC<{
  docType: any;
  purchaseRequestId: number | string;
  onUploaded: () => void;
}> = ({ docType, purchaseRequestId, onUploaded }) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState("");
  const hasUpload = docType.has_upload;
  const uploadedDocs = docType.uploaded_documents ?? [];
  const displayName =
    uploadedDocs[0]?.attachment_file_name ||
    uploadedDocs[0]?.document_name ||
    "Uploaded file";

  const handleFileChange = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const file = files[0];
    setIsUploading(true);
    setError("");
    try {
      const token = getToken();
      const accessRole = getUserRole();
      const formData = new FormData();
      formData.append("purchase_request", String(purchaseRequestId));
      formData.append("checklist_id", String(docType.checklist_id));
      formData.append("file", file);
      const response = await fetch(
        `${window.__RUNTIME_CONFIG__.REACT_APP_API_URL}inventory/api/v1/purchase-requests/upload-document-for-type/`,
        {
          method: "POST",
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...(accessRole ? { AccessRole: String(accessRole) } : {}),
          },
          body: formData,
        },
      );
      const result = await response.json();
      if (!response.ok) {
        throw new Error(result?.error || "Upload failed.");
      }
      onUploaded();
    } catch (err: any) {
      setError(err?.message || "Upload failed.");
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleDelete = async () => {
    if (!hasUpload || isDeleting) return;
    setIsDeleting(true);
    setError("");
    try {
      const token = getToken();
      const accessRole = getUserRole();
      const response = await fetch(
        `${window.__RUNTIME_CONFIG__.REACT_APP_API_URL}inventory/api/v1/purchase-requests/delete-document-for-type/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...(accessRole ? { AccessRole: String(accessRole) } : {}),
          },
          body: JSON.stringify({
            purchase_request: purchaseRequestId,
            checklist_id: docType.checklist_id,
          }),
        },
      );
      const result = await response.json();
      if (!response.ok) {
        throw new Error(result?.error || "Delete failed.");
      }
      onUploaded();
    } catch (err: any) {
      setError(err?.message || "Delete failed.");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div>
      <Input
        innerRef={fileInputRef}
        type="file"
        className="d-none"
        onChange={(e) => handleFileChange(e.target.files)}
        disabled={isUploading}
      />
      {hasUpload ? (
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <span className="text-success d-flex align-items-center gap-1 small">
            <i className="ri-check-line" />
            {displayName}
          </span>
          <Button
            color="danger"
            size="sm"
            outline
            onClick={handleDelete}
            disabled={isDeleting}
          >
            {isDeleting ? (
              <Spinner size="sm" />
            ) : (
              <>
                <i className="ri-delete-bin-line me-1" />
                Delete
              </>
            )}
          </Button>
        </div>
      ) : (
        <Button
          color="primary"
          size="sm"
          outline
          onClick={() => fileInputRef.current?.click()}
          disabled={isUploading}
        >
          {isUploading ? (
            <>
              <Spinner size="sm" className="me-1" />
              Uploading...
            </>
          ) : (
            <>
              <i className="ri-upload-2-line me-1" />
              Upload File
            </>
          )}
        </Button>
      )}
      {error && (
        <div className="text-danger mt-1" style={{ fontSize: 12 }}>
          {error}
        </div>
      )}
    </div>
  );
};

// ── Main Modal ────────────────────────────────────────────────────────────────
const PurchaseRequestModal: React.FC<Props> = ({
  isOpen,
  toggle,
  purchaseRequestData,
  cartItems,
  formErrors,
  isSubmitting,
  purchaseId,
  onChange,
  onSubmit,
}) => {
  const [step, setStep] = useState(1);
  const [productUploadStatus, setProductUploadStatus] = useState<
    Record<number, boolean>
  >({});
  const [isServerChecking, setIsServerChecking] = useState(false);
  const [documentsByType, setDocumentsByType] = useState<any[]>([]);
  const [documentsPreviewFetched, setDocumentsPreviewFetched] = useState(false);

  const steps = ["Upload Documents", "Review & Submit"];

  const productIds = Array.from(
    new Set(
      cartItems
        .map((item) => item.product?.id ?? item.product)
        .filter((id) => Boolean(id))
        .map((id) => Number(id)),
    ),
  );

  useEffect(() => {
    if (!isOpen || step !== 1 || productIds.length === 0) {
      if (!isOpen) {
        setDocumentsByType([]);
        setDocumentsPreviewFetched(false);
      }
      return;
    }
    const token = getToken();
    const accessRole = getUserRole();
    fetch(
      `${window.__RUNTIME_CONFIG__.REACT_APP_API_URL}inventory/api/v1/purchase-requests/required-documents-preview/`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(accessRole ? { AccessRole: String(accessRole) } : {}),
        },
        body: JSON.stringify({
          product_ids: productIds,
          ...(purchaseId ? { purchase_request_id: purchaseId } : {}),
        }),
      },
    )
      .then((res) => res.json())
      .then((result) => {
        setDocumentsByType(result?.documents_by_type ?? []);
        setDocumentsPreviewFetched(true);
      })
      .catch(() => setDocumentsByType([]));
  }, [isOpen, step, JSON.stringify(productIds), purchaseId]);

  const handleProductUploadStatus = (productId: number, done: boolean) => {
    setProductUploadStatus((prev) => ({ ...prev, [productId]: done }));
  };

  const refreshDocumentsPreview = () => {
    if (productIds.length === 0) return;
    const token = getToken();
    const accessRole = getUserRole();
    fetch(
      `${window.__RUNTIME_CONFIG__.REACT_APP_API_URL}inventory/api/v1/purchase-requests/required-documents-preview/`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(accessRole ? { AccessRole: String(accessRole) } : {}),
        },
        body: JSON.stringify({
          product_ids: productIds,
          ...(purchaseId ? { purchase_request_id: purchaseId } : {}),
        }),
      },
    )
      .then((res) => res.json())
      .then((result) => {
        setDocumentsByType(result?.documents_by_type ?? []);
      })
      .catch(() => {});
  };

  const useGroupedByType = Boolean(purchaseId && documentsByType.length > 0);

  const allDocsUploaded = useGroupedByType
    ? documentsByType.every((d) => d.has_upload)
    : cartItems.every(
        (item) => productUploadStatus[item.product?.id ?? item.product] === true,
      );

  const validateAndProceed = async () => {
    if (!allDocsUploaded) {
      alert("Please upload all required documents to proceed.");
      return;
    }

    const productIds = Array.from(
      new Set(
        cartItems
          .map((item) => item.product?.id ?? item.product)
          .filter((id) => Boolean(id))
          .map((id) => Number(id)),
      ),
    );

    if (productIds.length === 0) {
      setStep(2);
      return;
    }

    setIsServerChecking(true);
    try {
      const token = getToken();
      const accessRole = getUserRole();
      const response = await fetch(
        `${window.__RUNTIME_CONFIG__.REACT_APP_API_URL}inventory/api/v1/purchase-requests/required-documents-preview/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...(accessRole ? { AccessRole: String(accessRole) } : {}),
          },
          body: JSON.stringify({
            product_ids: productIds,
            ...(purchaseId ? { purchase_request_id: purchaseId } : {}),
          }),
        },
      );

      const result = await response.json();
      if (!response.ok) {
        const errorText =
          result?.error ||
          result?.details ||
          "Server validation failed. Please try again.";
        alert(errorText);
        return;
      }

      const missing = result?.missing_required_documents ?? [];
      if (Array.isArray(missing) && missing.length > 0) {
        const docs = missing
          .map((item: any) => item.document_name)
          .filter(Boolean);
        alert(
          docs.length > 0
            ? `Missing required uploads: ${docs.join(", ")}`
            : "Some required documents are still missing. Please upload and retry.",
        );
        return;
      }

      setStep(2);
    } catch (error) {
      alert("Unable to verify required documents from server. Please retry.");
    } finally {
      setIsServerChecking(false);
    }
  };

  const handleToggle = () => {
    setStep(1);
    setProductUploadStatus({});
    toggle();
  };

  return (
    <Modal isOpen={isOpen} toggle={handleToggle} size="xl">
      <ModalHeader toggle={handleToggle}>
        {purchaseId ? "Update Purchase Request" : "Submit Purchase Request"}
      </ModalHeader>

      <ModalBody style={{ maxHeight: "75vh", overflowY: "auto" }}>
        <StepIndicator currentStep={step} steps={steps} />

        {/* ── Step 1: Upload Documents ── */}
        {step === 1 && (
          <>
            {/* Cart Items Summary Table */}
            <div className="mb-1">
              <Label className="fw-semibold">Cart Items Summary</Label>
            </div>

            <div className="border rounded overflow-auto">
              {/* When updating an existing PR and the server returned documents_by_type,
                  we show one row per document type. A single upload satisfies all
                  products listed for that document. */}
              {useGroupedByType ? (
                <Table bordered className="mb-0 align-middle">
                  <thead className="table-light">
                    <tr>
                      <th style={{ minWidth: 180 }}>Document</th>
                      <th style={{ minWidth: 220 }}>Why this is required</th>
                      <th style={{ minWidth: 220 }}>Products included</th>
                      <th style={{ width: 140 }}>Upload</th>
                    </tr>
                  </thead>
                  <tbody>
                    {documentsByType.map((docType: any) => (
                      <tr key={docType.checklist_id}>
                        <td className="align-middle">
                          <div className="fw-semibold" style={{ fontSize: 13 }}>
                            {docType.document_name || "Required document"}
                            <span className="text-danger ms-1">*</span>
                          </div>
                          {docType.description && (
                            <div className="text-muted small">
                              {docType.description}
                            </div>
                          )}
                        </td>
                        <td className="align-middle">
                          <div style={{ fontSize: 12 }}>
                            {docType.mandatory_message ||
                              "You included these products so this document is mandatory to submit the request."}
                          </div>
                        </td>
                        <td className="align-middle">
                          <div style={{ fontSize: 12 }}>
                            {(docType.product_names || []).map(
                              (name: string, idx: number) => (
                                <div key={idx}>• {name}</div>
                              ),
                            )}
                          </div>
                        </td>
                        <td className="align-middle">
                          {purchaseId && (
                            <UploadByTypeCell
                              docType={docType}
                              purchaseRequestId={purchaseId as any}
                              onUploaded={refreshDocumentsPreview}
                            />
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <Table bordered className="mb-0 align-middle">
                  <thead className="table-light">
                    <tr>
                      <th className="ps-3" style={{ minWidth: 160 }}>
                        Product Name
                      </th>
                      <th>Documents</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cartItems.map((item) => (
                      <ProductChecklistRows
                        key={item.id ?? item.product?.id}
                        item={item}
                        purchaseRequestId={purchaseId}
                        onAllUploaded={handleProductUploadStatus}
                      />
                    ))}
                  </tbody>
                </Table>
              )}
            </div>

            {/* Detailed product list with images (for grouped-by-type view) */}
            {useGroupedByType && cartItems.length > 0 && (
              <div className="mt-3">
                <Label className="fw-semibold">Products in this request</Label>
                <div
                  className="border rounded"
                  style={{ maxHeight: "28vh", overflowY: "auto" }}
                >
                  <table className="table table-sm align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th style={{ width: 60 }}></th>
                        <th className="small fw-semibold">Product</th>
                        <th className="small fw-semibold text-center">Qty</th>
                        <th className="small fw-semibold text-center">
                          Category
                        </th>
                        <th className="small fw-semibold text-center">
                          Delivery
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {cartItems.map((item) => {
                        const productImage =
                          item?.product?.thumbnail_url ||
                          item?.product?.attachment_urls?.[0]?.file_url ||
                          item?.product?.attachment_urls?.[0]?.file ||
                          null;
                        return (
                          <tr key={item.id ?? item.product?.id}>
                            <td className="text-center">
                              <div
                                className="rounded border bg-light d-flex align-items-center justify-content-center mx-auto"
                                style={{
                                  width: 44,
                                  height: 44,
                                  overflow: "hidden",
                                  flexShrink: 0,
                                }}
                              >
                                {productImage ? (
                                  <img
                                    src={productImage}
                                    alt={item.product?.name || "Product"}
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                      objectFit: "cover",
                                    }}
                                  />
                                ) : (
                                  <i className="ri-image-line text-muted" />
                                )}
                              </div>
                            </td>
                            <td className="small">
                              <div className="fw-semibold">
                                {item.product?.name}
                              </div>
                              <div className="text-muted">
                                Unit: {item.product?.measurement_name || "-"}
                              </div>
                            </td>
                            <td className="text-center fw-bold text-primary">
                              {item.quantity}
                            </td>
                            <td className="text-center">
                              <span className="badge bg-success bg-opacity-10 text-success border-0 small">
                                {item.product?.category_name || "-"}
                              </span>
                            </td>
                            <td className="text-center small text-warning fw-semibold">
                              {item.expected_delivery_date
                                ? moment(item.expected_delivery_date).format(
                                    "MM/DD/YY",
                                  )
                                : "-"}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {!allDocsUploaded && cartItems.length > 0 && (
              <div className="alert alert-warning d-flex align-items-center gap-2 mt-3 py-2">
                <i className="ri-error-warning-line fs-5" />
                <span style={{ fontSize: 13 }}>
                  Please upload all required documents to proceed.
                </span>
              </div>
            )}
          </>
        )}

        {/* ── Step 2: Summary & Delivery Notes ── */}
        {step === 2 && (
          <>
            <Row>
              <Col md={12}>
                <div className="mb-3">
                  <Label>
                    Summary <span className="text-danger">*</span>
                  </Label>
                  <Input
                    name="summary"
                    value={purchaseRequestData.summary}
                    onChange={onChange}
                    invalid={!!formErrors.summary}
                    placeholder="Enter request summary"
                  />
                  <FormFeedback>{formErrors.summary}</FormFeedback>
                </div>
              </Col>
            </Row>

            <div className="mb-3">
              <Label>
                Delivery Address <span className="text-danger">*</span>
              </Label>
              <Input
                type="textarea"
                name="delivery_address"
                value={purchaseRequestData.delivery_address}
                onChange={onChange}
                rows={3}
                placeholder="Enter full delivery address"
                invalid={!!formErrors.delivery_address}
              />
              <FormFeedback>{formErrors.delivery_address}</FormFeedback>
            </div>

            <div className="mb-3">
              <Label>
                Delivery Notes
              </Label>
              <Input
                type="textarea"
                name="delivery_note"
                value={purchaseRequestData.delivery_note}
                onChange={onChange}
                rows={3}
                placeholder="Any special delivery instructions"
                invalid={!!formErrors.delivery_note}
              />
              <FormFeedback>{formErrors.delivery_note}</FormFeedback>
            </div>

            {/* Cart summary */}
            <div className="mb-3">
              <Label className="fw-semibold">Cart Items Summary</Label>
              <div
                className="border rounded"
                style={{ maxHeight: 200, overflowY: "auto" }}
              >
                <table className="table table-sm table-borderless align-middle mb-0">
                  <thead className="table-light sticky-top">
                    <tr>
                      <th className="ps-3 small fw-semibold">Product</th>
                      <th className="text-center small fw-semibold">Qty</th>
                      <th className="text-center small fw-semibold">
                        Category
                      </th>
                      <th className="text-center small fw-semibold">
                        Delivery
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {cartItems.map((item) => (
                      <tr
                        key={item.id ?? `temp-${item.product?.id}`}
                        className="border-bottom"
                      >
                        <td className="ps-3 small fw-semibold">
                          {item.product?.name}
                        </td>
                        <td className="text-center fw-bold text-primary">
                          {item.quantity}
                        </td>
                        <td className="text-center">
                          <span className="badge bg-success bg-opacity-10 text-success border-0 small">
                            {item.product?.category_name}
                          </span>
                        </td>
                        <td className="text-center small text-warning fw-semibold">
                          {moment(item.expected_delivery_date).format(
                            "MM/DD/YY",
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </ModalBody>

      <ModalFooter>
        <Button color="light" onClick={handleToggle}>
          Cancel
        </Button>

        {step === 1 && (
          <Button
            color="primary"
            disabled={!allDocsUploaded || isServerChecking}
            onClick={validateAndProceed}
          >
            {isServerChecking ? (
              <>
                <Spinner size="sm" className="me-2" />
                Verifying...
              </>
            ) : (
              <>
                <i className="ri-shield-check-line me-1" />
                Continue
              </>
            )}
          </Button>
        )}

        {step === 2 && (
          <>
            {/* <Button color="secondary" outline onClick={() => setStep(1)}>
              <i className="ri-arrow-left-line me-1" />
              Back
            </Button> */}
            <Button color="success" onClick={onSubmit} disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Submitting...
                </>
              ) : (
                <>
                  <i className="ri-check-line me-1" />
                  {purchaseId ? "Update Request" : "Submit Request"}
                </>
              )}
            </Button>
          </>
        )}
      </ModalFooter>
    </Modal>
  );
};

export default PurchaseRequestModal;